/**
 * Tag Management
 * Comprehensive tag and label management for merchants
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Tag, Plus, Trash2, Search, X, Check, AlertCircle, Loader,
  Users, TrendingUp, DollarSign, Calendar, Hash, ChevronDown, ChevronUp
} from 'lucide-react';
import './TagManagement.css';

const TAG_CATEGORIES = [
  {
    id: 'behavior',
    name: 'Behavior',
    icon: TrendingUp,
    color: '#3b82f6',
    description: 'Tags based on merchant behavior patterns like engagement level, purchase frequency, or risk assessment (e.g., high-value, engaged, at-risk, loyal, churning)'
  },
  {
    id: 'tier',
    name: 'Subscription Tier',
    icon: DollarSign,
    color: '#8b5cf6',
    description: 'Tags related to subscription levels or pricing tiers (e.g., pro-user, premium, basic, free-tier, enterprise)'
  },
  {
    id: 'activity',
    name: 'Activity',
    icon: Calendar,
    color: '#10b981',
    description: 'Tags based on account activity status and lifecycle stage (e.g., active, inactive, new-signup, dormant, reactivated)'
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: Hash,
    color: '#f59e0b',
    description: 'Custom tags for your specific business needs that don\'t fit other categories (e.g., beta-tester, vip-customer, needs-training)'
  }
];

function TagManagement({ apiWithAccessControl }) {
  const [tags, setTags] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmDeleteTag, setConfirmDeleteTag] = useState(null);

  // Inline status message
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success'|'error', text: '' }

  // New tag form
  const [newTag, setNewTag] = useState({
    name: '',
    category: 'custom',
    description: ''
  });

  useEffect(() => {
    fetchTags();
    fetchMerchants();
  }, []);

  // Auto-dismiss status messages after 5 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const showStatus = (type, text) => {
    setStatusMessage({ type, text });
  };

  const fetchTags = async () => {
    try {
      const response = await apiWithAccessControl('/admin/tags?type=user');
      const data = await response.json();
      if (data && data.tags) {
        const enhancedTags = data.tags.map(tagItem => ({
          name: tagItem.tag || tagItem.name,
          count: tagItem.count || 0,
          type: tagItem.type || 'user',
          category: inferTagCategory(tagItem.tag || tagItem.name),
          merchantCount: tagItem.count || 0
        }));
        setTags(enhancedTags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      showStatus('error', 'Failed to load tags. Please refresh the page.');
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await apiWithAccessControl('/admin/merchants?limit=1000');
      const data = await response.json();
      if (data && data.merchants) {
        setMerchants(data.merchants);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
      showStatus('error', 'Failed to load merchants.');
    } finally {
      setLoading(false);
    }
  };

  const inferTagCategory = (tagName) => {
    const name = tagName.toLowerCase();
    if (name.includes('tier') || name.includes('pro') || name.includes('basic') || name.includes('free')) {
      return 'tier';
    }
    if (name.includes('active') || name.includes('inactive') || name.includes('new') || name.includes('churn')) {
      return 'activity';
    }
    if (name.includes('high-value') || name.includes('engaged') || name.includes('at-risk')) {
      return 'behavior';
    }
    return 'custom';
  };

  const createTag = async () => {
    if (!newTag.name.trim()) {
      showStatus('error', 'Please enter a tag name.');
      return;
    }

    if (selectedMerchants.length === 0) {
      showStatus('error', 'Please select at least one merchant to tag.');
      return;
    }

    const tagName = newTag.name.trim().toLowerCase().replace(/\s+/g, '-');

    // Check for duplicate tag name
    if (tags.some(t => t.name === tagName)) {
      showStatus('error', `Tag "${tagName}" already exists. It will be assigned to the selected merchants.`);
    }

    setCreating(true);

    try {
      const response = await apiWithAccessControl('/admin/users/tags/bulk', {
        method: 'POST',
        body: JSON.stringify({
          userIds: selectedMerchants,
          tags: [tagName],
          action: 'add'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create and assign tag');
      }

      showStatus('success', `Tag "${tagName}" created and assigned to ${selectedMerchants.length} merchant(s).`);
      setShowCreateModal(false);
      setNewTag({ name: '', category: 'custom', description: '' });
      setSelectedMerchants([]);
      setMerchantSearch('');
      fetchTags();
      fetchMerchants();
    } catch (error) {
      console.error('Error creating tag:', error);
      showStatus('error', error.message || 'Failed to create tag.');
    } finally {
      setCreating(false);
    }
  };

  const deleteTag = async (tagName) => {
    setDeletingTag(tagName);

    try {
      const response = await apiWithAccessControl(`/admin/tags/${encodeURIComponent(tagName)}?type=user`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete tag');
      }

      const data = await response.json();
      showStatus('success', `Tag "${tagName}" deleted. Removed from ${data.usersUpdated || 0} merchant(s).`);
      setConfirmDeleteTag(null);
      fetchTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      showStatus('error', error.message || 'Failed to delete tag.');
    } finally {
      setDeletingTag(null);
    }
  };

  const getCategoryColor = (category) => {
    const cat = TAG_CATEGORIES.find(c => c.id === category);
    return cat ? cat.color : '#6b7280';
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = searchQuery === '' ||
      tag.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const tagsByCategory = TAG_CATEGORIES.reduce((acc, category) => {
    acc[category.id] = filteredTags.filter(tag => tag.category === category.id);
    return acc;
  }, {});

  // Filter merchants in modal by search
  const filteredMerchants = useMemo(() => {
    if (!merchantSearch.trim()) return merchants;
    const q = merchantSearch.toLowerCase();
    return merchants.filter(m =>
      (m.username || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q)
    );
  }, [merchants, merchantSearch]);

  const handleSelectAllMerchants = () => {
    const ids = filteredMerchants.map(m => m.id);
    const allSelected = ids.every(id => selectedMerchants.includes(id));
    if (allSelected) {
      setSelectedMerchants(selectedMerchants.filter(id => !ids.includes(id)));
    } else {
      setSelectedMerchants([...new Set([...selectedMerchants, ...ids])]);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setSelectedMerchants([]);
    setMerchantSearch('');
    setNewTag({ name: '', category: 'custom', description: '' });
  };

  return (
    <div className="mkt-tag-dashboard">
      {/* Inline Status Message */}
      {statusMessage && (
        <div className={`mkt-tag-status mkt-tag-status-${statusMessage.type}`}>
          {statusMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          <span>{statusMessage.text}</span>
          <button className="mkt-tag-status-close" onClick={() => setStatusMessage(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mkt-tag-header">
        <div>
          <h1>Tags & Labels</h1>
          <p>Create tags and assign them to merchants for better segmentation</p>
        </div>
        <div className="mkt-tag-header-actions">
          <button className="mkt-tag-btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Create & Assign Tag
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mkt-tag-filters">
        <div className="mkt-tag-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="mkt-tag-btn-icon" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>

        <div className="mkt-tag-category-filters">
          <button
            className={`mkt-tag-chip ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All Tags ({tags.length})
          </button>
          {TAG_CATEGORIES.map(category => {
            const Icon = category.icon;
            const count = tags.filter(t => t.category === category.id).length;
            return (
              <button
                key={category.id}
                className={`mkt-tag-chip ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
                style={{ borderColor: selectedCategory === category.id ? category.color : undefined }}
              >
                <Icon size={14} />
                {category.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Tags by Category */}
      {loading ? (
        <div className="mkt-tag-loading">
          <Loader size={24} className="mkt-tag-spinner" />
          <span>Loading tags...</span>
        </div>
      ) : filteredTags.length === 0 && !loading ? (
        <div className="mkt-tag-empty-state">
          <Tag size={48} />
          <h3>No tags found</h3>
          <p>
            {searchQuery || selectedCategory !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first tag to start organizing merchants.'}
          </p>
          {!searchQuery && selectedCategory === 'all' && (
            <button className="mkt-tag-btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Create Your First Tag
            </button>
          )}
        </div>
      ) : (
        <div className="mkt-tag-container">
          {TAG_CATEGORIES.map(category => {
            const categoryTags = tagsByCategory[category.id];
            if (categoryTags.length === 0 && selectedCategory !== 'all' && selectedCategory !== category.id) {
              return null;
            }

            const Icon = category.icon;

            return (
              <div key={category.id} className="mkt-tag-category-section">
                <div className="mkt-tag-category-header">
                  <Icon size={20} style={{ color: category.color }} />
                  <h2>{category.name}</h2>
                  <span className="mkt-tag-count">({categoryTags.length})</span>
                </div>

                {categoryTags.length === 0 ? (
                  <div className="mkt-tag-empty-category">
                    <Tag size={32} />
                    <p>No {category.name.toLowerCase()} tags yet</p>
                  </div>
                ) : (
                  <div className="mkt-tag-grid">
                    {categoryTags.map(tag => (
                      <div key={tag.name} className="mkt-tag-card">
                        <div className="mkt-tag-card-header">
                          <div className="mkt-tag-badge" style={{ backgroundColor: category.color + '20', color: category.color }}>
                            <Tag size={14} />
                            {tag.name}
                          </div>
                          <div className="mkt-tag-actions">
                            {confirmDeleteTag === tag.name ? (
                              <div className="mkt-tag-delete-confirm">
                                <span className="mkt-tag-delete-confirm-text">Delete?</span>
                                <button
                                  className="mkt-tag-btn-icon mkt-tag-btn-danger"
                                  title="Confirm delete"
                                  disabled={deletingTag === tag.name}
                                  onClick={() => deleteTag(tag.name)}
                                >
                                  {deletingTag === tag.name ? <Loader size={14} className="mkt-tag-spinner" /> : <Check size={14} />}
                                </button>
                                <button
                                  className="mkt-tag-btn-icon"
                                  title="Cancel"
                                  onClick={() => setConfirmDeleteTag(null)}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="mkt-tag-btn-icon mkt-tag-btn-danger-hover"
                                title="Delete tag"
                                onClick={() => setConfirmDeleteTag(tag.name)}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mkt-tag-stats">
                          <Users size={14} />
                          <span>{tag.merchantCount} merchant{tag.merchantCount !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Tag Modal */}
      {showCreateModal && (
        <div className="mkt-tag-modal-overlay" onClick={closeModal}>
          <div className="mkt-tag-modal large" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-tag-modal-header">
              <h2>Create & Assign Tag</h2>
              <button className="mkt-tag-btn-icon" onClick={closeModal}>
                <X size={18} />
              </button>
            </div>

            <div className="mkt-tag-modal-body">
              <div className="mkt-tag-form-group">
                <label>Tag Name *</label>
                <input
                  type="text"
                  placeholder="e.g., high-value, needs-onboarding"
                  value={newTag.name}
                  onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                />
                {newTag.name.trim() && (
                  <div className="mkt-tag-name-preview">
                    <Tag size={12} />
                    <span>Preview: <strong>{newTag.name.trim().toLowerCase().replace(/\s+/g, '-')}</strong></span>
                  </div>
                )}
                <small>Use lowercase with hyphens. Spaces will be converted automatically.</small>
              </div>

              <div className="mkt-tag-form-group">
                <label>Category *</label>
                <select
                  value={newTag.category}
                  onChange={(e) => setNewTag({...newTag, category: e.target.value})}
                >
                  {TAG_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <div className="mkt-tag-category-hint" style={{
                  backgroundColor: `${TAG_CATEGORIES.find(c => c.id === newTag.category)?.color}15`,
                  borderLeftColor: TAG_CATEGORIES.find(c => c.id === newTag.category)?.color
                }}>
                  <strong style={{ color: TAG_CATEGORIES.find(c => c.id === newTag.category)?.color }}>
                    {TAG_CATEGORIES.find(c => c.id === newTag.category)?.name}:
                  </strong>
                  {' '}
                  {TAG_CATEGORIES.find(c => c.id === newTag.category)?.description}
                </div>
              </div>

              <div className="mkt-tag-form-group">
                <label>Description (Optional)</label>
                <textarea
                  placeholder="What does this tag represent?"
                  value={newTag.description}
                  onChange={(e) => setNewTag({...newTag, description: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="mkt-tag-form-group">
                <label>Assign to Merchants * ({selectedMerchants.length} selected)</label>
                <div className="mkt-tag-merchant-search-bar">
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Search merchants by name or email..."
                    value={merchantSearch}
                    onChange={(e) => setMerchantSearch(e.target.value)}
                  />
                  {merchantSearch && (
                    <button className="mkt-tag-btn-icon" onClick={() => setMerchantSearch('')}>
                      <X size={12} />
                    </button>
                  )}
                </div>

                <div className="mkt-tag-merchant-toolbar">
                  <button
                    className="mkt-tag-select-all-btn"
                    onClick={handleSelectAllMerchants}
                  >
                    {filteredMerchants.length > 0 && filteredMerchants.every(m => selectedMerchants.includes(m.id))
                      ? 'Deselect All'
                      : `Select All (${filteredMerchants.length})`}
                  </button>
                  {selectedMerchants.length > 0 && (
                    <button
                      className="mkt-tag-clear-btn"
                      onClick={() => setSelectedMerchants([])}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                <div className="mkt-tag-merchants-list">
                  {filteredMerchants.length === 0 ? (
                    <div className="mkt-tag-empty-small">
                      <Users size={24} />
                      <p>{merchantSearch ? 'No merchants match your search' : 'No merchants found'}</p>
                    </div>
                  ) : (
                    filteredMerchants.map(merchant => (
                      <label key={merchant.id} className={`mkt-tag-merchant-checkbox ${selectedMerchants.includes(merchant.id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedMerchants.includes(merchant.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMerchants([...selectedMerchants, merchant.id]);
                            } else {
                              setSelectedMerchants(selectedMerchants.filter(id => id !== merchant.id));
                            }
                          }}
                        />
                        <span className="mkt-tag-merchant-name">{merchant.username || merchant.email}</span>
                        <span className="mkt-tag-merchant-tier">
                          {merchant.tier?.name || merchant.merchantTier || 'FREE'}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mkt-tag-modal-footer">
              <button className="mkt-tag-btn-secondary" onClick={closeModal} disabled={creating}>
                Cancel
              </button>
              <button
                className="mkt-tag-btn-primary"
                onClick={createTag}
                disabled={creating || selectedMerchants.length === 0 || !newTag.name.trim()}
              >
                {creating ? (
                  <>
                    <Loader size={16} className="mkt-tag-spinner" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Create & Assign to {selectedMerchants.length} Merchant{selectedMerchants.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TagManagement;
